import type { ColumnDef, Row } from '@tanstack/vue-table'
import { Button } from '~/components/ui/button'
import AlphabeticSortIcon from '~/components/AlphabeticSortIcon.vue'
import BarsArrowUp from '~/components/BarsArrowUp.vue'
import EnvelopeIcon from '~/components/EnvelopeIcon.vue'
import ProgressTrackerOwnerPendingEntriesModal from '~/components/ProgressTrackerOwnerPendingEntriesModal.vue'

export const createColumns = (
  companyId: number | string,
  queryParams: ProgressTrackerFilterParams | null = null,
  canEdit?: boolean,
): ColumnDef<PendingEntries>[] => {
  const columns: ColumnDef<PendingEntries>[] = [
    {
      accessorKey: 'data_owner_name',
      id: 'data_owner_name',
      header: ({ column }) => {
      // 'asc', 'desc', or undefined
        const isSorted = column.getIsSorted()
        const sortIconStyling = isSorted === 'asc' ? 'text-green-500' : isSorted === 'desc' ? 'text-red-500 rotate-180' : ''

        return h('div', { class: 'flex justify-between items-center w-full' }, [
          h('span', { class: 'text-sm text-slate-800 font-semibold capitalize' }, 'data owner'),
          h(Button, {
            class: 'text-main-primary',
            variant: 'ghost',
            onClick: () => column.toggleSorting(isSorted === 'asc', true),
          }, () => h(AlphabeticSortIcon, { customStyle: `size-5 ${sortIconStyling}` })),
        ])
      },
      cell: ({ row, column }) => h('div', { class: 'capitalize text-left text-md text-foreground no-localize-translate' }, row.getValue(column.id)),
    },
    {
      accessorKey: 'total_missing_data_points',
      id: 'total_missing_data_points',
      header: ({ column }) => {
      // 'asc', 'desc', or undefined
        const isSorted = column.getIsSorted()
        const sortIconStyling = isSorted === 'asc' ? 'text-green-500' : isSorted === 'desc' ? 'text-red-500 !rotate-0' : ''

        return h('div', { class: 'flex justify-between items-center w-full' }, [
          h('span', { class: 'text-sm text-slate-800 font-semibold capitalize' }, 'total missing data points'),
          h(Button, {
            class: 'text-main-primary',
            variant: 'ghost',
            onClick: () => column.toggleSorting(column.getIsSorted() === 'asc', true),
          }, () => h(BarsArrowUp, { customStyle: `size-5 rotate-180 ${sortIconStyling}` })),
        ])
      },
      cell: ({ row, column }) => h('div', { class: 'flex justify-between items-center w-full' }, [
        h('span', { class: 'text-sm text-slate-800 font-semibold' }, row.getValue(column.id)),
        h(ProgressTrackerOwnerPendingEntriesModal, {
          dataOwner: { name: row.original.data_owner_name, id: row.original.data_owner_id },
          missingDatapoints: row.original.total_missing_data_points,
          queryParams,
          canEdit,
        }),
      ]),
    },
  ]

  if (canEdit !== false) {
    columns.push({
      id: 'actions',
      header: () => h('span', { class: 'max-w-min text-sm text-slate-800 font-semibold capitalize' }, 'actions'),
      cell: ({ row }) => {
        return h(Button, {
          class: 'text-main-primary',
          variant: 'link',
          onClick: () => sendIndividualEmail(row, companyId, queryParams),
        }, () => h(EnvelopeIcon))
      },
    })
  }

  return columns
}

const sendIndividualEmail = (row: Row<PendingEntries>, companyId: number | string, queryParams: ProgressTrackerFilterParams | null) => {
  const { protocol, host } = window.location
  const { $apis, $toast } = useNuxtApp()
  const requestData = {
    ...(queryParams ? queryParams : {}),
    redirect_link: `${protocol}//${host}/dashboard/data-upload`,
  }

  $apis.progressTracker
    .sendPendingEntriesEmailReminder(companyId, row.original.data_owner_id, requestData)
    .then(() => $toast.success(`Processing an email notification to ${row.original.data_owner_name}`))
}
